import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { ApiPaths } from "../../../api/paths";
import CustomButton from "../../../components/custom-button/CustomButton";
import CustomInput from "../../../components/custom-input/CustomInput";
import useData from "../../../hooks/use-data";
import useMutateData from "../../../hooks/use-mutate-data";
import { StaffType } from "../../../types/staff";
import "./mutate-staff.css";
import CustomPhoneInput from "../../../components/custom-phone-input/CustomPhoneInput";
import CustomSelect from "../../../components/custom-select/CustomSelect";
import { useAppContext } from "../../../context/app-context";
import { PaginatedApiResponseType } from "../../../types/common";

interface Props {
  isEdit?: boolean;
  isProfile?: boolean;
}

export default function MutateStaff({
  isEdit = false,
  isProfile = false,
}: Props) {
  const { t } = useTranslation(["manageStaff", "common"]);
  const { role, user } = useAppContext();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data } = useData<StaffType>(`${ApiPaths.staff}/${userId}`, {
    fetchIf: isEdit,
  });

  let options: any = [];

  switch (role) {
    case "business-user": {
      options = [{ value: "business-user", label: "Business User" }];
      break;
    }

    case "business-admin": {
      options = [
        { value: "business-user", label: "Business User" },
        { value: "business-admin", label: "Business Admin" },
      ];
      break;
    }

    default: {
      options = [{ value: "individual", label: "Individual" }];
    }
  }

  const { data: companyList } = useData<PaginatedApiResponseType<StaffType[]>>(
    `https://api-dashboard.nautilusassure.io/v1/keycloak/companyList`,
    {
      onError: () => {
        toast(t("error.default", { ns: "common" }), {
          type: "error",
        });
      },
    }
  );

  const { loading: addLoading, mutate: addStaffMember } = useMutateData(
    ApiPaths.staff,
    {
      method: "POST",
      onSuccess: () => {
        toast(t("details.message.success_add", { ns: "manageStaff" }), {
          type: "success",
        });
        navigate(-1);
      },
      onError: () => {
        toast(t("error.default", { ns: "common" }), {
          type: "error",
          toastId: "staff-add",
        });
      },
    }
  );
  const { loading: updateLoading, mutate: updateStaffMember } = useMutateData(
    `${ApiPaths.staff}/${userId}`,
    {
      method: "PUT",
      onSuccess: () => {
        toast(t("details.message.success_edit", { ns: "manageStaff" }), {
          type: "success",
        });
        navigate(-1);
      },
      onError: () => {
        toast(t("error.default", { ns: "common" }), {
          type: "error",
          toastId: "staff-update",
        });
      },
    }
  );
  const initialValues = {
    firstName: isEdit ? data?.firstName : "",
    lastName: isEdit ? data?.lastName : "",
    email: isEdit ? data?.email : "",
    enabled: isEdit ? data?.enabled : "",
    company: isEdit
      ? data?.attributes?.companyName
        ? data?.attributes?.companyName[0]
        : ""
      : user?.attributes?.companyName
      ? user?.attributes?.companyName[0]
      : "",
    designation: isEdit
      ? data?.attributes?.designation
        ? data?.attributes?.designation[0]
        : ""
      : "",
    user_type: isEdit
      ? data?.attributes?.role
        ? data?.attributes?.role[0]
        : ""
      : "",
    phone: isEdit
      ? data?.attributes?.mobile
        ? data?.attributes?.mobile[0]
        : ""
      : "",
    userStatus: isEdit
      ? data?.attributes?.userStatus
        ? data?.attributes?.userStatus[0]
        : ""
      : "",
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(
      t("errors.first_name", { ns: "manageStaff" }) ?? ""
    ),
    lastName: Yup.string().required(
      t("errors.last_name", { ns: "manageStaff" }) ?? ""
    ),
    email: Yup.string().required(
      t("errors.email_id", { ns: "manageStaff" }) ?? ""
    ),
    user_type: Yup.string().required(
      t("errors.role", { ns: "manageStaff" }) ?? ""
    ),
    phone: Yup.string().required(
      t("errors.phone_number", { ns: "manageStaff" }) ?? ""
    ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (isEdit) {
        updateStaffMember({
          id: userId,
          firstName: values?.firstName,
          lastName: values?.lastName,
          email: values?.email,
          enabled: values?.enabled,
          attributes: {
            designation: values?.designation,
            mobile: values?.phone,
            role: values?.user_type,
            companyName: values?.company,
            userStatus: values?.userStatus,
          },
        });
        return;
      }
      addStaffMember({
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        company: values?.company,
        enabled: false,
        attributes: {
          designation: values?.designation,
          mobile: values?.phone,
          role: values?.user_type,
          companyName: values?.company,
          userStatus: "Pending",
        },
      });
    },
  });
  return (
    <div className="p-3 pb-5 p-md-4 mb-5">
      <div className="d-flex align-items-center justify-content-between mb-4">
        {isProfile ? (
          <h1 className="primary-title">
            {t("mutate_staff.heading.edit_profile", { ns: "manageStaff" })}
          </h1>
        ) : (
          <h1 className="primary-title">
            {isEdit
              ? t("mutate_staff.heading.edit", { ns: "manageStaff" })
              : t("mutate_staff.heading.add", { ns: "manageStaff" })}
          </h1>
        )}
        {role === "individual" || role === "business-user" ? (
          ""
        ) : (
          <Link
            className="primary-button text-uppercase  text-decoration-none"
            to="/manage-staff"
          >
            {t("mutate_staff.button.listing", { ns: "manageStaff" })}
          </Link>
        )}
      </div>
      <form className="row bg-white p-2 p-md-4" onSubmit={formik.handleSubmit}>
        <div className="col-md-6 mb-3">
          <CustomInput
            label={t("mutate_staff.label.first_name", {
              ns: "manageStaff",
            })}
            type="text"
            fieldName="firstName"
            formik={formik}
            key="firstName"
            required
          />
        </div>
        <div className="col-md-6 mb-3">
          <CustomInput
            label={t("mutate_staff.label.last_name", {
              ns: "manageStaff",
            })}
            type="text"
            fieldName="lastName"
            formik={formik}
            key="lastName"
            required
          />
        </div>
        <div className="col-md-6 mb-3">
          <CustomInput
            label={t("mutate_staff.label.designation", { ns: "manageStaff" })}
            type="text"
            fieldName="designation"
            formik={formik}
            key="designation"
          />
        </div>
        <div className="col-md-6 mb-3">
          <CustomSelect
            label={t("mutate_staff.label.user_type", { ns: "manageStaff" })}
            fieldName="user_type"
            formik={formik}
            key="user_type"
            required
          >
            <option value="">
              {t("mutate_staff.placeholder.user_type", {
                ns: "manageStaff",
              })}
            </option>
            {options.map((items: any) => (
              <option key={items.value} value={items.value}>
                {items.label}
              </option>
            ))}
          </CustomSelect>
        </div>
        {role === "individual" ? (
          ""
        ) : (
          <div className="col-md-6 mb-3">
            <CustomSelect
              label={t("mutate_staff.label.company", { ns: "manageStaff" })}
              fieldName="company"
              formik={formik}
              key="company"
              required
              disabled
            >
              <option value="">
                {t("mutate_staff.placeholder.company", {
                  ns: "manageStaff",
                })}
              </option>
              {companyList?.data?.map((items: any) => (
                <option key={items} value={items}>
                  {items}
                </option>
              ))}
            </CustomSelect>
          </div>
        )}
        <div className="col-md-6 mb-3">
          <CustomInput
            label={t("mutate_staff.label.email", { ns: "manageStaff" })}
            type="text"
            fieldName="email"
            formik={formik}
            key="email"
            required
            disabled={isEdit}
          />
        </div>
        <div className="col-md-6 mb-3">
          <CustomPhoneInput
            label={t("mutate_staff.label.phone_number", { ns: "manageStaff" })}
            fieldName="phone"
            formik={formik}
            key="phone"
            required
          />
        </div>
        <div className="col-12 mb-3">
          <div className="d-flex flex-row  justify-content-between justify-content-md-end">
            <button
              className="primary-button secondary-button text-decoration-none px-3 py-2 rounded-pill me-md-4"
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              {t("button.cancel", { ns: "common" })}
            </button>
            <CustomButton
              className="primary-button px-3 py-2 rounded-pill"
              type="submit"
              loading={addLoading || updateLoading}
            >
              {isProfile
                ? t("button.update_profile", { ns: "manageStaff" })
                : isEdit
                ? t("button.edit", { ns: "manageStaff" })
                : t("button.add", { ns: "manageStaff" })}
            </CustomButton>
          </div>
        </div>
      </form>
    </div>
  );
}
